/**
 * OnboardingDialog
 *
 * The 3-step onboarding survey as a modal dialog ON TOP of the app — the
 * former `/setup` page is gone. The user lands on the real app (defaultRoute
 * `/invoice-conversion`), sees it dimmed behind the backdrop, answers three
 * questions and is dropped straight into the warm page. No route change, no
 * reload.
 *
 * Non-dismissible on purpose: the survey has its own skip step ("Direkt zur
 * App", which submits an empty answer set). Escape and backdrop clicks are
 * swallowed so there is exactly one way out — `disablePointerDismissal` kills
 * the backdrop click, `showCloseButton={false}` removes the X, and an
 * `onOpenChange` that ignores every close request kills Escape. The dialog
 * closes only when `open` flips, i.e. when `onboardingCompleted` is written.
 *
 * Mounted by OnboardingGuard, which sits inside ConsentGuard and therefore
 * re-mounts per page — the draft in ONBOARDING_STORAGE keeps the intermediate
 * state, so a re-mount resumes at the current step.
 */

import { OnboardingFlow } from '@xhubio-saas/core-frontend/plugin-onboarding-yaml'
import { Dialog, DialogContent } from '@xhubio-saas/core-frontend/ui-components'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { onboardingConfigs } from '../onboarding/configs'
import { ONBOARDING_STORAGE } from '../onboarding/storage'

interface OnboardingDialogProps {
  /** Controlled: true until preferences.onboardingCompleted flips to true. */
  open: boolean
}

/**
 * Swallow every close request (Escape, programmatic dismiss). Base UI treats a
 * controlled `open` whose `onOpenChange` does nothing as "cannot be closed" —
 * which is exactly the intent here.
 */
function ignoreCloseRequest() {
  // intentionally empty — see component docblock
}

export function OnboardingDialog({ open }: OnboardingDialogProps) {
  const { i18n } = useTranslation()
  const locale: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en'
  const config = onboardingConfigs[locale]
  const flowRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog open={open} disablePointerDismissal onOpenChange={ignoreCloseRequest}>
      {/*
        p-0/gap-0: DialogContent's default p-6 would stack on top of
        OnboardingFlow's own p-6. The flow keeps its padding (it is the same
        6er step the dialog would apply), so the visual result is identical to
        the old page — just inside a 2xl-wide popup.

        initialFocus on the flow container (not Base UI's default = first
        tabbable): the first tabbable is the "Direkt zur App" skip, and a
        reflexive Enter right after registration would silently skip the
        whole survey.
      */}
      {/*
        aria-label instead of a DialogTitle: the flow renders its own visible
        <h1>, and a sr-only <h2> before it inverted the heading order (audit
        finding). The label satisfies Base UI's accessible-name requirement.
      */}
      <DialogContent
        className="max-w-2xl gap-0 p-0"
        showCloseButton={false}
        initialFocus={flowRef}
        aria-label={config.title}
      >
        {/*
          Wrapper classes: onboarding-yaml-flow = theme-bridge scope of the
          engine; onboarding-dialog-flow = dialog-only de-shelling of the
          engine's inner Card (see index.css) — the dialog is the only shell.
          showStepper={false}: with 3 steps the stepper added a second progress
          signal next to the "Schritt n" counter, duplicated the step title,
          and its total jumped 2→3 once the choice resolved (audit findings).
        */}
        <div
          className="onboarding-yaml-flow onboarding-dialog-flow outline-none"
          ref={flowRef}
          tabIndex={-1}
        >
          <OnboardingFlow
            config={config}
            storage={ONBOARDING_STORAGE}
            showStepper={false}
            onComplete={async (answers) => {
              // The listener lives above RouterProvider, so the bridge stays a
              // window event (see OnboardingCompletionListener).
              window.dispatchEvent(
                new CustomEvent('onboarding-yaml:complete', { detail: { answers } })
              )
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
